// Shared client-side validation for lead-magnet forms.
// Used by LeadMagnetDialog (hero) and ExitIntentModal so the rules stay in sync.
import { z } from "zod";

// RFC-pragmatic email regex (matches what HTML5 type=email accepts in practice).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Allow + ( ) - . space and digits; require at least 7 digits overall.
const PHONE_RE = /^[+\d\s().-]+$/;

export const LeadFormSchema = z.object({
  first_name: z
    .string()
    .trim()
    .min(1, "First name is required")
    .min(2, "Use at least 2 characters")
    .max(80, "First name is too long")
    .regex(/^[\p{L}\p{M}'.\- ]+$/u, "Letters, spaces, and ' . - only"),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .max(255, "Email is too long")
    .regex(EMAIL_RE, "Enter a valid email (you@practice.com)"),
  phone: z
    .string()
    .trim()
    .max(32, "Phone is too long")
    .refine(
      (v) => v === "" || (PHONE_RE.test(v) && (v.match(/\d/g)?.length ?? 0) >= 7),
      "Enter a valid phone or leave blank",
    )
    .optional()
    .or(z.literal("")),
});

export type LeadFormValues = z.infer<typeof LeadFormSchema>;
export type LeadFormField = keyof LeadFormValues;
export type LeadFormErrors = Partial<Record<LeadFormField, string>>;

export function validateLeadForm(values: LeadFormValues): {
  ok: boolean;
  errors: LeadFormErrors;
  data?: LeadFormValues;
} {
  const parsed = LeadFormSchema.safeParse(values);
  if (parsed.success) return { ok: true, errors: {}, data: parsed.data };
  const errors: LeadFormErrors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path[0] as LeadFormField | undefined;
    if (key && !errors[key]) errors[key] = issue.message;
  }
  return { ok: false, errors };
}

// Validate a single field — used for onBlur inline feedback.
export function validateField(field: LeadFormField, value: string): string | undefined {
  const single = LeadFormSchema.shape[field].safeParse(value);
  return single.success ? undefined : single.error.issues[0]?.message;
}
